/**
 * Gedeelde afhandeling voor knoppen die iets muteren.
 *
 * Eén plek voor: bezig-status (knop disabled), succes- en foutmelding,
 * bevestigingsvraag en het herladen van de betrokken lijst na afloop —
 * precies wat de huidige bundle per knop met de hand doet.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from './api'

export interface Toast { id: number; text: string; ok: boolean; onClick?: () => void; groen?: boolean; waarschuwing?: boolean }

/** 18-10-2026 (Jordy): een melding kan bij een klik iets openen (het gesprek).
 *  `groen`: de fan antwoordde in een gesprek dat nu van MIJ is (multi-chatter inbox). */
export interface NotifyOpts {
  onClick?: () => void
  groen?: boolean
  /** 18-10-2026 (Jordy): opvallend (amber), bv. "2 minutes left to answer …". */
  waarschuwing?: boolean
  /** Hoe lang de melding blijft staan (ms); standaard 5 s. */
  duur?: number
}

interface ActionsCtx {
  toasts: Toast[]
  notify: (text: string, ok?: boolean, opts?: NotifyOpts) => void
  /** Voert een mutatie uit met bevestiging, melding en herladen. */
  run: (opts: {
    label: string
    confirm?: string
    request: () => Promise<unknown>
    onDone?: () => void
    successText?: string
  }) => Promise<boolean>
  busy: string | null
}

const Ctx = createContext<ActionsCtx | null>(null)

export function ActionsProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const [busy, setBusy] = useState<string | null>(null)

  const notify = useCallback((text: string, ok = true, opts?: NotifyOpts) => {
    const id = Date.now() + Math.random()
    setToasts((t) => [...t, { id, text, ok, onClick: opts?.onClick, groen: opts?.groen, waarschuwing: opts?.waarschuwing }])
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), opts?.duur ?? 5000)
  }, [])

  const weg = useCallback((id: number) => setToasts((x) => x.filter((y) => y.id !== id)), [])

  const run = useCallback<ActionsCtx['run']>(async (opts) => {
    if (opts.confirm && !window.confirm(opts.confirm)) return false
    setBusy(opts.label)
    try {
      await opts.request()
      notify(opts.successText ?? `${opts.label}: done`)
      opts.onDone?.()
      return true
    } catch (e) {
      notify(`${opts.label}: ${e instanceof Error ? e.message : String(e)}`, false)
      return false
    } finally {
      setBusy(null)
    }
  }, [notify])

  const value = useMemo(() => ({ toasts, notify, run, busy }), [toasts, notify, run, busy])

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* Rechtsonder, zoals oud — niet midden onderaan. Daar dekken ze de
          knoppenbalk van het chatvenster af. Elke melding heeft een kruisje:
          een melding over een nieuw bericht mag je wegklikken zonder vijf
          seconden te wachten. */}
      <div className="toasts">
        {toasts.map((t) => (
          <div key={t.id} className={['toast', t.onClick ? 'klik' : '', t.groen ? 'groen' : '', t.waarschuwing ? 'waarschuwing' : ''].filter(Boolean).join(' ')}
               style={t.ok ? undefined : { borderColor: 'rgba(248,113,113,.45)' }}
               role={t.onClick ? 'button' : undefined} tabIndex={t.onClick ? 0 : undefined}
               title={t.onClick ? 'Open this chat' : undefined}
               onClick={t.onClick ? () => { weg(t.id); t.onClick?.() } : undefined}
               onKeyDown={t.onClick ? (e) => { if (e.key === 'Enter') { weg(t.id); t.onClick?.() } } : undefined}>
            <span style={{ flex: 1, color: t.ok ? 'var(--txt)' : '#fca5a5' }}>
              {t.text}
            </span>
            <button type="button" className="sluit" aria-label="Dismiss" title="Dismiss"
                    onClick={(e) => { e.stopPropagation(); weg(t.id) }}>
              ✕
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}

export function useActions(): ActionsCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useActions buiten ActionsProvider')
  return c
}

/** Kortere schrijfwijze voor de meest voorkomende mutaties. */
export const mut = {
  patch: (path: string, body?: unknown) => () => api.patch(path, body),
  post: (path: string, body?: unknown) => () => api.post(path, body),
  put: (path: string, body?: unknown) =>
    () => fetch(path, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('admin_access_token') || ''}`,
      },
      body: JSON.stringify(body ?? {}),
    }).then(async (r) => {
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || `HTTP ${r.status}`)
      return r.status === 204 ? null : r.json().catch(() => null)
    }),
  del: (path: string) => () => api.del(path),
}
